import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calculator, Wallet, Home, Target, Sailboat } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const calcTabs = [
  { id: "sip", label: "SIP", Icon: Calculator },
  { id: "lumpsum", label: "Lumpsum", Icon: Wallet },
  { id: "retirement", label: "Retirement", Icon: Sailboat },
  { id: "goal", label: "Goal", Icon: Target },
  { id: "emi", label: "EMI", Icon: Home },
];

function formatINR(n) {
  if (!isFinite(n)) n = 0;
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function Slider({ label, value, set, min, max, step, fmt, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{label}</label>
        <span className="text-lg font-bold text-white font-mono">{fmt(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-blue-500"
      />
    </div>
  );
}

export default function Calculators() {
  const [tab, setTab] = useState("sip");

  return (
    <section id="calculators" className="relative py-28 px-6 lg:px-8 bg-[#070B1A]">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-12">
          <span className="section-label">Calculators</span>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mt-5">
            Visualise the <span className="gradient-text">compound</span> effect
          </h2>
          <p className="mt-5 text-slate-400 text-base sm:text-lg">
            Move the sliders — watch your future wealth respond in real time.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="calc-tabs">
          {calcTabs.map((c) => (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              data-testid={`calc-tab-${c.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab === c.id
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                  : "glass text-slate-300 hover:text-white"
              }`}
            >
              <c.Icon className="w-4 h-4" />
              {c.label}
            </button>
          ))}
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {tab === "sip" && <SIPCalc />}
          {tab === "lumpsum" && <LumpsumCalc />}
          {tab === "retirement" && <RetirementCalc />}
          {tab === "goal" && <GoalCalc />}
          {tab === "emi" && <EMICalc />}
        </motion.div>
      </div>
    </section>
  );
}

function ChartCard({ data, dataKey = "value", label = "Projected Value" }) {
  return (
    <div className="glass-strong rounded-2xl p-6 h-full">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">{label}</div>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="gAvg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
          <XAxis dataKey="year" stroke="#475569" fontSize={11} />
          <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
          <Tooltip
            contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
            formatter={(v) => formatINR(v)}
          />
          <Area type="monotone" dataKey={dataKey} stroke="#60A5FA" strokeWidth={2.5} fill="url(#gAvg)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SIPCalc() {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);
  const { data, total, invested, gain } = useMemo(() => {
    const r = rate / 100 / 12;
    const data = [];
    let invested = 0, fv = 0;
    for (let y = 1; y <= years; y++) {
      const n = y * 12;
      fv = monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      invested = monthly * n;
      data.push({ year: `Y${y}`, value: Math.round(fv), invested: Math.round(invested) });
    }
    return { data, total: fv, invested, gain: fv - invested };
  }, [monthly, years, rate]);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-2xl p-7 space-y-6">
        <Slider label="Monthly Investment" value={monthly} set={setMonthly} min={500} max={200000} step={500} fmt={formatINR} testid="sip-monthly" />
        <Slider label="Duration (Years)" value={years} set={setYears} min={1} max={40} step={1} fmt={(v) => `${v} yrs`} testid="sip-years" />
        <Slider label="Expected Return p.a." value={rate} set={setRate} min={4} max={25} step={0.5} fmt={(v) => `${v}%`} testid="sip-rate" />
        <div className="pt-4 border-t border-white/10 space-y-3">
          <Row k="Total Invested" v={formatINR(invested)} />
          <Row k="Estimated Gain" v={formatINR(gain)} c="text-emerald-400" />
          <Row k="Maturity Value" v={formatINR(total)} big />
        </div>
      </div>
      <div className="lg:col-span-3">
        <ChartCard data={data} />
      </div>
    </div>
  );
}

function LumpsumCalc() {
  const [amount, setAmount] = useState(500000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const { data, total, gain } = useMemo(() => {
    const data = [];
    let fv = 0;
    for (let y = 1; y <= years; y++) {
      fv = amount * Math.pow(1 + rate / 100, y);
      data.push({ year: `Y${y}`, value: Math.round(fv) });
    }
    return { data, total: fv, gain: fv - amount };
  }, [amount, years, rate]);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-2xl p-7 space-y-6">
        <Slider label="Investment Amount" value={amount} set={setAmount} min={10000} max={10000000} step={10000} fmt={formatINR} testid="lump-amount" />
        <Slider label="Duration (Years)" value={years} set={setYears} min={1} max={40} step={1} fmt={(v) => `${v} yrs`} testid="lump-years" />
        <Slider label="Expected Return p.a." value={rate} set={setRate} min={4} max={25} step={0.5} fmt={(v) => `${v}%`} testid="lump-rate" />
        <div className="pt-4 border-t border-white/10 space-y-3">
          <Row k="Invested" v={formatINR(amount)} />
          <Row k="Estimated Gain" v={formatINR(gain)} c="text-emerald-400" />
          <Row k="Maturity Value" v={formatINR(total)} big />
        </div>
      </div>
      <div className="lg:col-span-3"><ChartCard data={data} /></div>
    </div>
  );
}

function RetirementCalc() {
  const [age, setAge] = useState(30);
  const [retire, setRetire] = useState(60);
  const [monthly, setMonthly] = useState(20000);
  const [rate, setRate] = useState(12);
  const years = Math.max(1, retire - age);
  const { data, total } = useMemo(() => {
    const r = rate / 100 / 12;
    const data = [];
    let fv = 0;
    for (let y = 1; y <= years; y++) {
      const n = y * 12;
      fv = monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      data.push({ year: `Age ${age + y}`, value: Math.round(fv) });
    }
    return { data, total: fv };
  }, [years, monthly, rate, age]);
  const monthlyIncome = total * 0.04 / 12;

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-2xl p-7 space-y-6">
        <Slider label="Current Age" value={age} set={setAge} min={18} max={60} step={1} fmt={(v) => `${v} yrs`} testid="ret-age" />
        <Slider label="Retirement Age" value={retire} set={setRetire} min={Math.max(age + 1, 40)} max={75} step={1} fmt={(v) => `${v} yrs`} testid="ret-age2" />
        <Slider label="Monthly SIP" value={monthly} set={setMonthly} min={1000} max={200000} step={1000} fmt={formatINR} testid="ret-monthly" />
        <Slider label="Return p.a." value={rate} set={setRate} min={6} max={20} step={0.5} fmt={(v) => `${v}%`} testid="ret-rate" />
        <div className="pt-4 border-t border-white/10 space-y-3">
          <Row k="Retirement Corpus" v={formatINR(total)} big />
          <Row k="Monthly Income (4% rule)" v={formatINR(monthlyIncome)} c="text-emerald-400" />
        </div>
      </div>
      <div className="lg:col-span-3"><ChartCard data={data} label="Corpus Growth" /></div>
    </div>
  );
}

function GoalCalc() {
  const [goal, setGoal] = useState(5000000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(12);
  const monthly = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    return goal / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
  }, [goal, years, rate]);
  const data = useMemo(() => {
    const r = rate / 100 / 12;
    const arr = [];
    let fv = 0;
    for (let y = 1; y <= years; y++) {
      const n = y * 12;
      fv = monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      arr.push({ year: `Y${y}`, value: Math.round(fv) });
    }
    return arr;
  }, [monthly, years, rate]);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-2xl p-7 space-y-6">
        <Slider label="Goal Amount" value={goal} set={setGoal} min={100000} max={50000000} step={100000} fmt={formatINR} testid="goal-amount" />
        <Slider label="Time to Goal (Years)" value={years} set={setYears} min={1} max={30} step={1} fmt={(v) => `${v} yrs`} testid="goal-years" />
        <Slider label="Expected Return p.a." value={rate} set={setRate} min={4} max={20} step={0.5} fmt={(v) => `${v}%`} testid="goal-rate" />
        <div className="pt-4 border-t border-white/10 space-y-3">
          <Row k="Goal" v={formatINR(goal)} />
          <Row k="Required Monthly SIP" v={formatINR(monthly)} big c="text-blue-400" />
        </div>
      </div>
      <div className="lg:col-span-3"><ChartCard data={data} /></div>
    </div>
  );
}

function EMICalc() {
  const [loan, setLoan] = useState(3000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const { emi, totalInt, totalPay, data } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    const emi = (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPay = emi * n;
    const totalInt = totalPay - loan;
    const data = [];
    let bal = loan;
    for (let y = 1; y <= years; y++) {
      for (let m = 0; m < 12; m++) {
        const interest = bal * r;
        const principal = emi - interest;
        bal -= principal;
      }
      data.push({ year: `Y${y}`, value: Math.max(0, Math.round(bal)) });
    }
    return { emi, totalInt, totalPay, data };
  }, [loan, rate, years]);

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 glass-strong rounded-2xl p-7 space-y-6">
        <Slider label="Loan Amount" value={loan} set={setLoan} min={100000} max={20000000} step={50000} fmt={formatINR} testid="emi-loan" />
        <Slider label="Interest Rate p.a." value={rate} set={setRate} min={5} max={18} step={0.1} fmt={(v) => `${v.toFixed(1)}%`} testid="emi-rate" />
        <Slider label="Tenure (Years)" value={years} set={setYears} min={1} max={30} step={1} fmt={(v) => `${v} yrs`} testid="emi-years" />
        <div className="pt-4 border-t border-white/10 space-y-3">
          <Row k="Monthly EMI" v={formatINR(emi)} big c="text-blue-400" />
          <Row k="Total Interest" v={formatINR(totalInt)} c="text-amber-400" />
          <Row k="Total Payment" v={formatINR(totalPay)} />
        </div>
      </div>
      <div className="lg:col-span-3"><ChartCard data={data} label="Outstanding Balance" /></div>
    </div>
  );
}

function Row({ k, v, big, c }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-slate-400">{k}</span>
      <span className={`${big ? "text-2xl" : "text-base"} font-bold font-mono ${c || "text-white"}`}>{v}</span>
    </div>
  );
}
